// Centralized session & embed token management utilities
// Ensures single source of truth for expiry derivation & timeout handling.

import { useState, useEffect } from "react";

export interface EmbedDataShape {
  token?: string;
  embedUrl?: string;
  reportId?: string;
  expiryAtMs?: number;
  expiresIn?: number;
  tokenExpiry?: string | number;
  expiresAt?: string | number;
  expiration?: string | number;
  [k: string]: any;
}

export const EMBED_DATA_KEY = "connecthqEmbedData";
export const EXPIRED_FLAG_KEY = "connecthqExpired";

function decodeJwtExp(token?: string): number | undefined {
  if (!token) return undefined;
  const parts = token.split(".");
  if (parts.length < 2) return undefined;
  try {
    const payloadJson = atob(parts[1].replace(/-/g, "+").replace(/_/g, "/"));
    const payload = JSON.parse(payloadJson || "{}");
    if (typeof payload.exp === "number") {
      return payload.exp > 1e12 ? payload.exp : payload.exp * 1000;
    }
  } catch {}
  return undefined;
}

export function getExpiryFromData(data: EmbedDataShape): number | undefined {
  if (!data) return undefined;
  const now = Date.now();
  const candidates: number[] = [];
  const add = (v: any) => {
    if (v == null) return;
    if (typeof v === "number") candidates.push(v > 1e12 ? v : v * 1000);
    else if (typeof v === "string") {
      const t = Date.parse(v);
      if (!isNaN(t)) candidates.push(t);
    }
  };
  add(data.expiryAtMs);
  add(data.tokenExpiry);
  add(data.expiresAt);
  add(data.expiration);
  if (typeof data.expiresIn === "number")
    candidates.push(now + data.expiresIn * 1000);
  const jwtExp = decodeJwtExp(data.token);
  if (jwtExp) candidates.push(jwtExp);
  const future = candidates.filter((c) => c > now);
  if (future.length) return Math.min(...future);
  return data.token ? now + 30 * 60 * 1000 : undefined;
}

export function getStoredEmbedData(): EmbedDataShape | null {
  try {
    const raw = localStorage.getItem(EMBED_DATA_KEY);
    if (!raw) return null;
    const parsed: EmbedDataShape = JSON.parse(raw);
    if (parsed?.token && parsed.embedUrl && parsed.reportId) return parsed;
  } catch {}
  return null;
}

export function cacheEmbedData(data: EmbedDataShape): void {
  if (!data?.token || !data.embedUrl || !data.reportId) return;
  try {
    const expiry = getExpiryFromData(data);
    if (expiry) data.expiryAtMs = expiry;
    localStorage.setItem(EMBED_DATA_KEY, JSON.stringify(data));
  } catch {}
}

export function clearSession(): void {
  try {
    localStorage.removeItem(EMBED_DATA_KEY);
    localStorage.removeItem(EXPIRED_FLAG_KEY);
  } catch {}
}

export function hasExpiredFlag(): boolean {
  try {
    return !!localStorage.getItem(EXPIRED_FLAG_KEY);
  } catch {
    return false;
  }
}

function setExpiredFlag(): void {
  try {
    localStorage.setItem(EXPIRED_FLAG_KEY, "1");
  } catch {}
}

export function clearExpiredFlag(): void {
  try {
    localStorage.removeItem(EXPIRED_FLAG_KEY);
  } catch {}
}

export function isTokenExpired(
  data: EmbedDataShape | null | undefined
): boolean {
  if (!data?.token) return true;
  const exp = getExpiryFromData(data);
  if (!exp) return false;
  return exp <= Date.now();
}

export function useSessionTimeout(
  embedData: EmbedDataShape | null | undefined
) {
  const [expired, setExpired] = useState<boolean>(hasExpiredFlag());

  useEffect(() => {
    if (!embedData?.token) return;
    const exp = getExpiryFromData(embedData);
    if (!exp) return;
    if (exp <= Date.now()) {
      setExpired(true);
      setExpiredFlag();
      return;
    }
    const handle = window.setTimeout(() => {
      setExpired(true);
      setExpiredFlag();
    }, Math.max(exp - Date.now(), 0));
    return () => window.clearTimeout(handle);
  }, [embedData, embedData?.token]);

  return { expired };
}
